import API from '@/services/api';
import { connect } from 'react-redux';
import FONTS from '@/assets/fonts.json';
import { isDEV } from '@/utils/Location';
import { User, MCD } from '@/services/app';
import { useState, useEffect } from 'react';
// import { isAndroid } from '@CS/utils/Device';
import { Routes, Route } from 'react-router-dom';

import { getLogged } from '@/redux/auth/selectors';
import type { UserState } from '@/redux/user/types';
import CloseButton from '@CS/components/CloseButton';
import { getUserState } from '@/redux/user/selectors';
import { useNavigateQuery, useRecaptcha } from '@/hooks';
import { useFonts, useLoader, useImages } from '@CS/hooks';
import { Login, Landing, Overview, Editor } from '@/routes';
import { AppWrapper, ContentWrapper } from '@CS/app/App.styles';
import { Misc, Hair, Face, Shirt, Pants } from '@/assets/images';
import TranslationProvider from '@CS/components/TranslationProvider';

type AppProps = {
  user: UserState;
  logged?: boolean;
};

const App = ({ user, logged }: AppProps) => {
  const imagesLoaded = useImages({
    ...Misc,
    ...Hair,
    ...Face,
    ...Shirt,
    ...Pants
  });

  const fontsLoaded = useFonts(FONTS);
  const navigate = useNavigateQuery();

  const { recaptcha } = useRecaptcha();

  const onLoad = () => setFetchingData(false);
  const [loading, setLoading] = useState(true);

  const { email, /* points, */ country } = user ?? {};
  const [fetchingData, setFetchingData] = useState(false);

  const onVisibilityChange = () =>
    document.visibilityState === 'visible' && toggleFullscreen();

  const loader = useLoader(
    loading || !fontsLoaded || !recaptcha || !imagesLoaded || fetchingData
  );

  const getUserData = async () => {
    // await User.checkUserPoints();
    await User.checkUserData();
    setLoading(false);
  };

  const toggleFullscreen = () => {
    if (isDEV()) return;
    MCD.toggleFullscreen();
    MCD.orientationScreen();
  };

  const initApplication = () => {
    User.checkUserLanguage();
    User.checkUserCountry();
    toggleFullscreen();
    handleAuth();
  };

  const loginRedirect = () => {
    setLoading(false);
    navigate('/login');
  };

  const onLogin = async () => {
    toggleFullscreen();
    await getUserData();
    setFetchingData(true);
    navigate('/');
  };

  const homeRedirect = () => {
    setLoading(false);
    setFetchingData(true);
    !isDEV() && navigate('/');
  };

  const handleAuth = () =>
    !email /* || typeof points !== 'number' */ &&
    getUserData().then(homeRedirect).catch(loginRedirect);

  useEffect(() => {
    const userInfo = country && email;
    const ready = recaptcha && !logged;
    ready && userInfo && API.login(user);
  }, [recaptcha, logged, country, email, user]);

  useEffect(() => {
    MCD.app.mcd && !loading && initApplication();
  }, [MCD.app.mcd]);

  useEffect(() => {
    document.addEventListener('mcdBridgeReady', initApplication);
    (MCD.app.mcd || isDEV()) && initApplication();

    return () =>
      document.removeEventListener('mcdBridgeReady', initApplication);
  }, []);

  useEffect(() => {
    // if (!isAndroid) {
    //   return;
    // }

    document.addEventListener('visibilitychange', onVisibilityChange);
    document.addEventListener('focus', onVisibilityChange);
    window.addEventListener('focus', onVisibilityChange);

    return () => {
      document.addEventListener('visibilitychange', onVisibilityChange);
      document.addEventListener('focus', onVisibilityChange);
      window.addEventListener('focus', onVisibilityChange);
    };
  }, []);

  return (
    <AppWrapper id="app-wrapper">
      <CloseButton />

      <TranslationProvider lang={'nl'}>
        <ContentWrapper>
          <Routes>
            <Route path="/login" element={<Login onLogin={onLogin} />} />
            <Route path="/" element={<Landing onLogin={onLogin} />} />

            <Route path="/overview" element={<Overview onLoad={onLoad} />} />
            <Route path="/editor" element={<Editor onLoad={onLoad} />} />
          </Routes>
        </ContentWrapper>
      </TranslationProvider>

      {loader}
    </AppWrapper>
  );
};

const mapStateToProps = (state: never) => ({
  user: getUserState(state),
  logged: getLogged(state)
});

export default connect(mapStateToProps)(App);
